import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataPointService } from 'src/app/project/shared/services/datapoint.service';
import { Chart } from 'chart.js/auto';
import { ReportService } from 'src/app/dashboard/shared/services/report.service';
import { SelectInput } from 'src/app/shared/dropdown/shared/select-input.class';
import { ReportingService } from 'src/app/reporting/shared/services/reporting.service';
import { NewReportingFilter } from 'src/app/reporting/shared/interfaces/new-reporting-filter';

@Component({
  selector: 'avs-speed-chart',
  templateUrl: './speed-chart.component.html',
  styleUrls: ['./speed-chart.component.css']
})

export class SpeedChartComponent implements OnInit, OnDestroy {
  public samId: number;
  public laneDescription: string = "";
  public deviceName: string = "";
  public now = new Date();
  public nowAsISOString: string;
  public twoDaysAgoAsString: string;
  public chartData: any;
  public labelCollection: string[] = [];
  public resultCollection: number[] = [];
  public fontSize: number = 10;

  public lkwAverageSpeedCollection: number[] = [];
  public pkwAverageSpeedCollection: number[] = [];
  public kfzTrafficVolumeCollection: number[] = [];
  public lkwTrafficVolumeCollection: number[] = [];
  public pkwTrafficVolumeCollection: number[] = [];

  public isChannelDropdownOpen: boolean = false;
  public channelSelect: SelectInput[] = [];
  public selectedChannel: SelectInput = { name: '', id: 0 };

  public isIntervallDropdownOpen: boolean = false;
  public intervallSelect: SelectInput[] = [{ name: '5 Minuten', id: 5 }, { name: '15 Minuten', id: 15 }, { name: '30 Minuten', id: 30 }, { name: '60 Minuten', id: 60 }];
  public selectedIntervall: SelectInput = { name: '5 Minuten', id: 5 };

  public fromDate: number = 2;
  public toDate: number = 0;
  public selectedMinutes: number = 60;
  public minutes: number[] = [5, 15, 30, 60];
    
  public dataAge: number = 0;
  private chartIds = ['SpeedChart', 'SpeedChart2'];

  constructor(@Inject(MAT_DIALOG_DATA) private data: any,
    private dataPointService: DataPointService,
    private reportService: ReportService,
    private reportingService: ReportingService,
    private dialogRef: MatDialogRef<SpeedChartComponent>) {
    let radars = (this.data.data.schemaInfo?.radars ?? []).filter((r: any) => r.data != null)
    let lastData = new Date(radars[0]?.data?.dataDateTime ?? NaN)
    let today = new Date()
    let diff = today.getTime() - lastData.getTime()
    let diffDays = diff / (1000 * 60 * 60 * 24)
    this.dataAge = diffDays
    if (this.dataAge !== undefined && this.dataAge > 1) { this.toDate = Math.round(this.dataAge); this.fromDate = Math.round(this.dataAge) + 2 }
    this.samId = this.data.data.routeStation.samId
    this.deviceName = this.data.data.routeStation.name;
    this.nowAsISOString = this.now.toISOString()
    let d = new Date();
    d.setDate(d.getDate() - 2);
    this.twoDaysAgoAsString = d.toISOString()
    if (radars.length > 0) {
      let length = radars.length /* die condition kann ich als disabled für den kanal dropdown nutzen */
      for (let i = 0; i < length; i++) {
        let de = radars[i].cInfo.channel.de;
        this.channelSelect.push({ name: 'Kanal ' + de, id: de })
        
        if (i == 0){
          this.selectedChannel.name = this.channelSelect[i].name
          this.selectedChannel.id = this.channelSelect[i].id
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.chartIds.forEach(id => Chart.getChart(id)?.destroy());
  }

  ngOnInit(): void {
    this.getData();
  }

  public exportChart(): void {
    let canvas = document.getElementById('SpeedChart') as HTMLCanvasElement;
    let dataURL = canvas.toDataURL('image/png');
    let link = document.createElement('a');
    link.href = dataURL;
    link.download = `verkehrsdaten-${this.samId}-${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  }

  private getData(): void {
    this.labelCollection = [];
    this.lkwAverageSpeedCollection = [];
    this.pkwAverageSpeedCollection = [];
    this.kfzTrafficVolumeCollection = [];
    this.lkwTrafficVolumeCollection = [];
    this.pkwTrafficVolumeCollection = [];


    /*  */
    let filter: NewReportingFilter = {
      samId: this.samId,
      utcFrom: this.twoDaysAgoAsString,
      utcTo: this.nowAsISOString,
      minutes: Number(this.selectedIntervall.id),
      deChannels: [Number(this.selectedChannel.id)],

    };
    this.reportingService.getTrafficDataGraphics(filter).subscribe((response: any[]) => {

      // response is an array of objects (one per station/channel)
      // Each has .data, which is an array of measurements
      if (response.length > 0 && response[0].data && Array.isArray(response[0].data)) {
        this.laneDescription = response[0].lane;
        const chartData = response[0].data;

        chartData.forEach((element: any) => {
          // Use timestampFrom or timestampTo as your label
          const timestamp = element.timestampFrom || element.timestampTo;
          // Format date as "dd.MM.yy HH:mm"
          const dateObj = new Date(timestamp);
          const formattedDate = dateObj
            ? `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getFullYear()).substring(2)} ${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}`
            : '';
          this.labelCollection.push(formattedDate);

          // Collect traffic data
          this.lkwAverageSpeedCollection.push(element.trafficData.lkwAverageSpeed);
          this.pkwAverageSpeedCollection.push(element.trafficData.pkwAverageSpeed);
          this.kfzTrafficVolumeCollection.push(element.trafficData.kfzTrafficVolume);
          this.lkwTrafficVolumeCollection.push(element.trafficData.lkwTrafficVolume);
          this.pkwTrafficVolumeCollection.push(element.trafficData.pkwTrafficVolume);
        });
      }
      this.createChart();
    });
  }

  public onChannelItemClicked(item: SelectInput): void {
    this.isChannelDropdownOpen = false;
    this.selectedChannel.name = item.name
    this.selectedChannel.id = item.id
    this.removeData();
  }

  public onChannelDropdownClicked() {
    this.isChannelDropdownOpen = !this.isChannelDropdownOpen;
  }

  public onIntervallItemClicked(item: SelectInput): void {
    this.isIntervallDropdownOpen = false;
    this.selectedIntervall.name = item.name
    this.selectedIntervall.id = item.id
    this.removeData();
  }

  public onIntervallDropdownClicked() {
    this.isIntervallDropdownOpen = !this.isIntervallDropdownOpen;
  }

  
  public removeData(): void {
    this.destroyCharts()
    this.getData();
  }
  private destroyCharts(): void {
    this.chartIds.forEach(id => {
      const chartInstance = Chart.getChart(id);
      if (chartInstance) {
        chartInstance.destroy();
      }
    });
  }

  public setFromDate(date: number): void {
    this.fromDate = date
    let d = new Date();
    d.setDate(d.getDate() - this.fromDate);
    this.twoDaysAgoAsString = d.toISOString()
  }

  public setToDate(date: number): void {
    this.toDate = date
    let d = new Date();
    d.setDate(d.getDate() - this.toDate);
    this.nowAsISOString = d.toISOString()
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();
  }

  private createChart(): void {
    new Chart("SpeedChart", {
      type: 'line',
      data: {
        labels: this.labelCollection,
        datasets: [
          {
            label: "Durchschnittsgeschwindigkeit LKW in km/h",
            data: this.lkwAverageSpeedCollection,
            backgroundColor: 'blue',
            fill: false,
            borderColor: 'rgba(0, 0, 255, 0.198)',
            tension: 0.1,
            pointRadius: 2
          },
          {
            label: "Durchschnittsgeschwindigkeit PKW in km/h",
            data: this.pkwAverageSpeedCollection,
            backgroundColor: 'red',
            fill: false,
            borderColor: 'rgba(255, 0, 0, 0.198)',
            tension: 0.1,
            pointRadius: 2
          },
          {
            label: "Anzahl KFZ",
            data: this.kfzTrafficVolumeCollection,
            backgroundColor: 'green',
            fill: false,
            borderColor: 'rgba(0, 255, 255, 0.198)',
            tension: 0.1,
            pointRadius: 2
          },
          {
            label: "Anzahl LKW",
            data: this.lkwTrafficVolumeCollection,
            backgroundColor: 'yellow',
            fill: false,
            borderColor: 'rgba(0, 255, 0, 0.198)',
            tension: 0.1,
            pointRadius: 2
          },
          {
            label: "Anzahl PKW",
            data: this.pkwTrafficVolumeCollection,
            backgroundColor: 'black',
            fill: false,
            borderColor: 'rgba(0, 0, 0, 0.198)',
            tension: 0.1,
            pointRadius: 2
          },


        ]
      },
      options: {
        aspectRatio: 2.5,
      },
      plugins: [
        {
          id: 'customCanvasBackgroundColor',
          beforeDraw: function (chart: any) {
            const ctx = chart.ctx;
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        }
      ]
    });
  }
}
